import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { X, Loader2, Search } from 'lucide-react';
import { useCreateClassroom } from './hooks/useCreateClassroom';
import { useNavigate } from 'react-router-dom';
import { useMembersApi } from '@/features/members';

const cn = (...classes) => classes.filter(Boolean).join(' ');

// --- Icon & Illustration Components ---

const LightbulbIcon = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}>
    <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 9 6c0 1.3.5 2.6 1.5 3.5.7.8 1.3 1.5 1.5 2.5" />
    <path d="M9 18h6" />
    <path d="M10 22h4" />
  </svg>
);

const PlusIcon = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}>
    <path d="M5 12h14" />
    <path d="M12 5v14" />
  </svg>
);

const UsersIcon = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const GraduationCapIcon = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}>
    <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
    <path d="M6 12v5c3 3 9 3 12 0v-5" />
  </svg>
);

const StaffIllustration = ({ className }) => (
  <svg className={className} viewBox="0 0 150 100" xmlns="http://www.w3.org/2000/svg">
    <g transform="translate(10, 5)">
      <rect x="25" y="15" width="100" height="60" rx="5" className="fill-muted" />
      <rect x="30" y="20" width="90" height="50" className="fill-background" />
      <path d="M 65 75 L 85 75 L 90 85 L 60 85 Z" className="fill-muted-foreground/20" />
      <rect x="40" y="45" width="10" height="20" className="fill-emerald-400" />
      <rect x="55" y="35" width="10" height="30" className="fill-rose-400" />
      <rect x="70" y="50" width="10" height="15" className="fill-blue-400" />
      <rect x="85" y="25" width="10" height="40" className="fill-yellow-400" />
      <g transform="translate(90, 10) rotate(15)">
        <rect x="0" y="0" width="40" height="25" rx="3" className="fill-background stroke-border" strokeWidth="1" />
        <rect x="5" y="5" width="10" height="5" className="fill-primary/20" />
        <rect x="5" y="15" width="30" height="2" className="fill-muted" />
        <rect x="5" y="20" width="20" height="2" className="fill-muted" />
      </g>
    </g>
  </svg>
);

const StudentsIllustration = ({ className }) => (
  <svg className={className} viewBox="0 0 150 100" xmlns="http://www.w3.org/2000/svg">
    <g transform="translate(20, 10)">
      <circle cx="30" cy="25" r="8" className="fill-primary/20" />
      <rect x="22" y="35" width="16" height="20" rx="8" className="fill-primary/10" />
      <circle cx="50" cy="20" r="10" className="fill-secondary/30" />
      <rect x="40" y="32" width="20" height="25" rx="10" className="fill-secondary/20" />
      <circle cx="75" cy="25" r="8" className="fill-accent/30" />
      <rect x="67" y="35" width="16" height="20" rx="8" className="fill-accent/20" />
      <circle cx="95" cy="22" r="9" className="fill-muted-foreground/20" />
      <rect x="86" y="33" width="18" height="22" rx="9" className="fill-muted-foreground/10" />
      <rect x="25" y="65" width="80" height="4" rx="2" className="fill-primary/30" />
      <rect x="35" y="72" width="60" height="3" rx="1.5" className="fill-secondary/30" />
      <rect x="45" y="78" width="40" height="3" rx="1.5" className="fill-accent/30" />
    </g>
  </svg>
);

// --- Main Component ---

function CreateClassPage() {
  const [activeSection, setActiveSection] = useState('details');
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    class_name: '',
    subject: '',
    grade_id: '',
  });

  const [selectedStaff, setSelectedStaff] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState(''); // 'staff' or 'students'
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const { createClassroom, loading, error } = useCreateClassroom();
  const { getMembers } = useMembersApi();

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const fetchMembersByRole = async (role) => {
    setMembersLoading(true);
    try {
      console.log('Calling getMembers API with role:', role);
      const data = await getMembers({ role });
      console.log('Received members data:', data);
      setMembers(data);
    } catch (err) {
      console.error('Error fetching members:', err);
    } finally {
      setMembersLoading(false);
    }
  };

  const openMemberDialog = async (type) => {
    console.log('Opening member dialog for:', type);
    setDialogType(type);
    setDialogOpen(true);
    setSearchTerm('');

    const role = type === 'staff' ? 'staff' : 'member';
    console.log('Fetching members with role:', role);
    await fetchMembersByRole(role);
  };

  const handleMemberToggle = (member, checked) => {
    if (dialogType === 'staff') {
      setSelectedStaff((prev) => (checked ? [...prev, member] : prev.filter((m) => m.id !== member.id)));
    } else {
      setSelectedStudents((prev) => (checked ? [...prev, member] : prev.filter((m) => m.id !== member.id)));
    }
  };

  const isMemberSelected = (member) => {
    const selectedList = dialogType === 'staff' ? selectedStaff : selectedStudents;
    return selectedList.some((m) => m.id === member.id);
  };

  const filteredMembers = members.filter(
    (member) =>
      member.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const SidebarItem = ({ id, label, icon: Icon, isCompleted }) => (
    <button
      onClick={() => scrollToSection(id)}
      className={cn(
        'w-full text-left px-4 py-3 rounded-md text-sm font-medium transition-colors flex items-center gap-3',
        activeSection === id
          ? 'bg-primary text-primary-foreground'
          : 'hover:bg-muted text-muted-foreground hover:text-foreground'
      )}>
      <Icon className="w-4 h-4" />
      <span className="flex-1">{label}</span>
      {isCompleted && (
        <Badge variant="secondary" className="text-xs">
          ✓
        </Badge>
      )}
    </button>
  );

  const handleSubmit = async () => {
    const payload = {
      class_name: formData.class_name,
      subject: formData.subject,
      grade_id: formData.grade_id,
      staff: selectedStaff.map((member) => ({ id: member.id, name: member.name, email: member.email })),
      students: selectedStudents.map((member) => ({ id: member.id, name: member.name, email: member.email })),
    };

    const result = await createClassroom(payload);

    if (result.success) {
      console.log('Class created successfully:', result.data);
      navigate('/admin/classes');
    } else {
      console.error('Error creating class:', result.error);
    }
  };

  const isDetailsCompleted = formData.class_name && formData.subject && formData.grade_id;

  return (
    <div className="h-screen bg-background font-sans flex flex-col overflow-hidden">
      <div className="p-6 border-b border-border">
        <h1 className="text-3xl font-bold tracking-tight text-foreground">Create a class</h1>
        <p className="text-muted-foreground mt-2">Set up your new class by filling in the details below</p>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <aside className="w-80 p-6 border-r border-border">
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Setup Progress
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <SidebarItem id="details" label="Basic details" icon={LightbulbIcon} isCompleted={isDetailsCompleted} />
              <SidebarItem id="staff" label="Add staff" icon={UsersIcon} />
              <SidebarItem id="students" label="Add students" icon={GraduationCapIcon} />
            </CardContent>
          </Card>
        </aside>

        <ScrollArea className="h-full w-full">
          <div className="space-y-8 p-6">
            <Card id="details" className="scroll-mt-6 bg-muted/40">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <LightbulbIcon className="h-5 w-5 text-primary" />
                  Basic details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-4">
                  <div className="flex flex-col items-center text-center">
                    <Label className="mb-3 text-sm font-medium">Class logo</Label>
                    <div className="flex h-24 w-24 cursor-pointer items-center justify-center rounded-full border-2 border-dashed border-muted-foreground/25 transition-colors group hover:border-primary/50 hover:bg-muted/50">
                      <LightbulbIcon className="h-8 w-8 text-muted-foreground transition-colors group-hover:text-primary" />
                    </div>
                    <Button variant="ghost" size="sm" className="mt-2 text-xs">
                      Upload image
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:col-span-3">
                    <div className="space-y-2">
                      <Label htmlFor="class_name" className="text-sm font-medium">
                        Class name <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="class_name"
                        placeholder="Enter class name"
                        value={formData.class_name}
                        onChange={(e) => setFormData({ ...formData, class_name: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="subject" className="text-sm font-medium">
                        Subject <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="subject"
                        placeholder="Enter subject name"
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      />
                    </div>

                    <div className="sm:col-span-2 space-y-2">
                      <Label htmlFor="grade_id" className="text-sm font-medium">
                        Grade ID <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="grade_id"
                        placeholder="Enter grade ID"
                        value={formData.grade_id}
                        onChange={(e) => setFormData({ ...formData, grade_id: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card id="staff" className="scroll-mt-6 bg-muted/40">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <UsersIcon className="h-5 w-5 text-primary" />
                  Add staff members
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col items-center justify-center py-12 text-center border-2 border-dashed border-muted-foreground/25 rounded-lg bg-muted/20 hover:bg-muted/30 transition-colors">
                  <StaffIllustration className="w-32 h-20 mb-4 opacity-60" />
                  <h3 className="text-lg font-semibold mb-2">Invite staff members</h3>
                  <p className="text-muted-foreground mb-6 max-w-md">
                    Add teachers, assistants, and other staff members to help manage your class
                  </p>
                  <Button
                    className="gap-2"
                    onClick={() => {
                      console.log('Staff button clicked!');
                      openMemberDialog('staff');
                    }}
                  >
                    <PlusIcon className="h-4 w-4" />
                    Add staff members
                  </Button>
                  {selectedStaff.length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm text-muted-foreground mb-2">Selected staff ({selectedStaff.length}):</p>
                      <div className="flex flex-wrap gap-2">
                        {selectedStaff.map((staff) => (
                          <Badge key={staff.id} variant="secondary" className="flex items-center gap-1">
                            {staff.name || staff.email}
                            <X
                              className="h-3 w-3 cursor-pointer"
                              onClick={() => setSelectedStaff((prev) => prev.filter((s) => s.id !== staff.id))}
                            />
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card id="students" className="scroll-mt-6 bg-muted/40">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <GraduationCapIcon className="h-5 w-5 text-primary" />
                  Add students
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col items-center justify-center py-12 text-center border-2 border-dashed border-muted-foreground/25 rounded-lg bg-muted/20 hover:bg-muted/30 transition-colors">
                  <StudentsIllustration className="w-32 h-20 mb-4 opacity-60" />
                  <h3 className="text-lg font-semibold mb-2">Invite students</h3>
                  <p className="text-muted-foreground mb-6 max-w-md">
                    Add students to your class by sending invitations or sharing a class code
                  </p>
                  <Button
                    className="gap-2"
                    onClick={() => {
                      console.log('Students button clicked!');
                      openMemberDialog('students');
                    }}
                  >
                    <PlusIcon className="h-4 w-4" />
                    Add students
                  </Button>
                  {selectedStudents.length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm text-muted-foreground mb-2">
                        Selected students ({selectedStudents.length}):
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {selectedStudents.map((student) => (
                          <Badge key={student.id} variant="secondary" className="flex items-center gap-1">
                            {student.name || student.email}
                            <X
                              className="h-3 w-3 cursor-pointer"
                              onClick={() => setSelectedStudents((prev) => prev.filter((s) => s.id !== student.id))}
                            />
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <div className="h-24"></div>
          </div>
        </ScrollArea>
      </div>

      <div className="p-6 border-t border-border bg-background">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="text-sm text-muted-foreground">
            {error && <span className="text-destructive">Error: {error.message || 'Failed to create class'}</span>}
            {!error && isDetailsCompleted ? (
              <span className="text-emerald-600">✓ Ready to create class</span>
            ) : (
              <span>Complete the required fields to create your class</span>
            )}
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => navigate('/admin/classes')}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={!isDetailsCompleted || loading}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create class'
              )}
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Select {dialogType === 'staff' ? 'Staff Members' : 'Students'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search members..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            <ScrollArea className="max-h-96">
              {membersLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                  <span className="ml-2">Loading members...</span>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">Select</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredMembers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-6 text-muted-foreground">
                          No members found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredMembers.map((member) => (
                        <TableRow key={member.id}>
                          <TableCell>
                            <Checkbox
                              checked={isMemberSelected(member)}
                              onCheckedChange={(checked) => handleMemberToggle(member, checked)}
                            />
                          </TableCell>
                          <TableCell className="font-medium">{member.name || 'N/A'}</TableCell>
                          <TableCell>{member.email}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{member.role || 'member'}</Badge>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              )}
            </ScrollArea>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => setDialogOpen(false)}>
              Done ({dialogType === 'staff' ? selectedStaff.length : selectedStudents.length} selected)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default CreateClassPage;
